import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import Col from 'react-bootstrap/Col';
import Container from 'react-bootstrap/Container';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import {useTranslation} from 'react-i18next';
import RecordName from "./RecordName";
import RecordDescription from './RecordDescription';
import './RecordForm.css';
import RecordEndDate from './RecordEndDate';
import RecordCollections from "./RecordCollections";
import RecordSubtitle from "./RecordSubtitle";
import RecordLicense from './RecordLicense';
import RecordSpokenLanguage from './RecordSpokenLanguage';
import RecordCollectionKeywords from './RecordCollectionKeywords';
import CollectionKeywords from '../collection/keyword/CollectionKeywords';
import ContentTypes from '../form/ContentTypes';
import ScienceDropdown from '../form/ScienceDropdown';
import RecordCollectionContentType from './RecordCollectionContentType';
import RecordSubtitleDownloadLinks from './RecordSubtitleDownloadLinks';
import useVideos from '../../hooks/useVideos';
import RecordChapters from './RecordChapters';
import RecordChapterDownloadLinks from './RecordChapterDownloadLinks';
import Toggle from '../form/Toggle';
import ElementHeader from '../form/ElementHeader';
import HelpDialog from '../dialog/HelpDialog';

const RecordForm = ({
                        record,
                        vttFiles,
                        validationMessages,
                        onChange,
                        onSubtitleChange,
                        resetSubtitleDownloadLinks,
                        refreshChapters,
                        disabled
                    }) => {
    const {t} = useTranslation();
    const [inheritedScience, setInheritedScience] = useState(null);
    const [inheritedScienceLoadedFor, setInheritedScienceLoadedFor] = useState(null);
    const [inheritedCollection, setInheritedCollection] = useState(null);
    const [addChapters, setAddChapters] = useState(() =>
        Object.values(record.chapters || {}).some(Boolean)
    );
    const videos = useVideos(record.identifier);
    const subtitles = videos?.flatMap(video => video.vttFiles || []).filter(Boolean);
    const selectedCollectionId = record.isPartOf || record.is_part_of;

    const hasRecordContentType =
        typeof record.contentType === 'string' && record.contentType.trim().length > 0;

    const [selectedContentType, setSelectedContentType] = useState(() => {
        return hasRecordContentType ? record.contentType : null;
    });

    useEffect(() => {
        let isMounted = true;

        const load = async () => {
            setInheritedCollection(null);
            if (!selectedCollectionId) {
                if (isMounted) {
                    setInheritedScience(null);
                    setInheritedScienceLoadedFor(null);
                }
                return;
            }

            try {
                const URL = `${process.env.REACT_APP_LATAAMO_PROXY_SERVER}/api/series/${selectedCollectionId}`;
                const response = await fetch(URL);
                if (!response.ok) throw new Error(`Unexpected status code from ${URL}`);
                const collection = await response.json();

                if (!isMounted) return;
                setInheritedCollection(collection);
                setInheritedScience(collection?.science ?? null);
                setInheritedScienceLoadedFor(String(selectedCollectionId));
            } catch (e) {
                if (!isMounted) return;
                setInheritedCollection(null);
                setInheritedScience(null);
                setInheritedScienceLoadedFor(String(selectedCollectionId));
            }
        };

        load();
        return () => {
            isMounted = false;
        };
    }, [selectedCollectionId]);

    return (
        <Container className="ps-0 record-form">
            <div className="record-form-section">
                <h2 className="record-form-section-header record-form-section-header-required">
                    <span>{t('record_form_section_basic_info')}</span>
                    <span className="record-required-fields-note">{t('record_required_fields_note')}</span>
                </h2>
                <Row className="mb-2">
                    <Col>
                        <RecordName
                            name={record.title}
                            onChange={
                                (title) => onChange('title', title)
                            }
                            message={validationMessages?.title}
                            disabled={disabled}
                        />
                    </Col>
                </Row>
                <Row className="mb-2">
                    <Col>
                        <RecordDescription
                            description={record.description}
                            onChange={
                                (description) => onChange('description', description)
                            }
                            message={validationMessages?.description}
                            disabled={disabled}
                        />
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <RecordLicense
                            license={record.license}
                            onChange={
                                (license) => onChange('license', license)
                            }
                            message={validationMessages?.license}
                            disabled={disabled}
                        />
                    </Col>
                </Row>
            </div>
            <div className="record-form-section">
                <h2 className="record-form-section-header">{t('record_form_section_publishing')}</h2>
                <Row className="mb-2">
                    <Col>
                        <RecordEndDate
                            endDate={record.deletionDate}
                            onChange={
                                (date) => onChange('deletionDate', date)
                            }
                            message={validationMessages?.deletionDate}
                            disabled={disabled}
                        />
                    </Col>
                </Row>
            </div>
                <Row>
                    <Col>
                        <RecordCollections
                            collection={record.isPartOf || record.is_part_of}
                            onChange={(value) => onChange('isPartOf', value)}
                            disabled={disabled}
                        />
                    </Col>
                </Row>
            <div className="record-form-section">
                <h2 className="record-form-section-header">
                    {t('categorization_form_header')}
                </h2>
                <Row className="mb-2">
                    <Col>
                        <ContentTypes
                            afterHeader={(
                                <div className="mb-2">
                                    <RecordCollectionContentType
                                        id={selectedCollectionId}
                                        collection={inheritedScienceLoadedFor === String(selectedCollectionId) ? inheritedCollection : null}
                                        loading={Boolean(selectedCollectionId) && inheritedScienceLoadedFor !== String(selectedCollectionId)}
                                    />
                                </div>
                            )}
                            onChange={(contentType) => {
                                setSelectedContentType(contentType);
                                onChange('contentType', contentType);
                            }}
                            disabled={disabled}
                            message={validationMessages?.contentType}
                            selected={selectedContentType}
                            required={false}
                            defaultOptionLabelKey="record_content_type_option_default_select"
                        />
                    </Col>
                </Row>
                <Row className="mb-2">
                    <Col>
                        <ScienceDropdown
                            className="edit-record-science-dropdown"
                            selected={record.science ?? ''}
                            inheritedScience={inheritedScience}
                            inheritedScienceLoading={Boolean(selectedCollectionId) && inheritedScienceLoadedFor !== String(selectedCollectionId)}
                            placeholder={t('science_option_change')}
                            required={false}
                            onChange={(science) => onChange('science', science)}
                            disabled={disabled}
                            message={validationMessages?.science}
                        />
                    </Col>
                </Row>
                <Row className="mb-2">
                    <Col xs={12} md={6}>
                        <RecordCollectionKeywords
                            id={record.isPartOf || record.is_part_of}
                            collection={inheritedScienceLoadedFor === String(selectedCollectionId) ? inheritedCollection : null}
                        />
                    </Col>
                    <Col xs={12} md={6}>
                        <CollectionKeywords
                            keywords={record.keywords?.map(kw => kw.label)}
                            onKeywordChange={(keywords) => onChange('keywords', keywords.map(kw => ({label: kw})))}
                            disabled={disabled}
                        />
                    </Col>
                </Row>
            </div>
            <div className="record-form-section">
                <h2 className="record-form-section-header">{t('record_form_section_language_subtitles')}</h2>
                <Row className="mb-2">
                    <Col>
                        <RecordSpokenLanguage
                            language={record.language}
                            onChange={(value) => onChange('language', value)}
                            disabled={disabled}
                            message={validationMessages?.language}
                        />
                    </Col>
                </Row>
                <Row className="mb-2">
                    <Col>
                        <RecordSubtitle
                            onChange={(value) => onChange('selectedSubtitles', value)}
                            message={validationMessages?.selectedSubtitles}
                            subtitles={record.selectedSubtitles}
                            vttFiles={vttFiles}
                            disabled={disabled}
                            language={record.language}
                        />
                    </Col>
                </Row>
                <Container>
                    <Form.Group>
                        <Row className="record-subtitle-row mb-2">
                            <Col>
                                <ElementHeader
                                    label={t('record_chapters_header')}
                                    helpDialog={(
                                        <HelpDialog label={t('record_chapters_help_label')}>
                                            {t('record_chapters_help_content')}
                                        </HelpDialog>
                                    )}
                                >
                                    {t('record_chapters_header')}
                                </ElementHeader>
                            </Col>
                        </Row>
                        <Row className="mb-2">
                            <Row>
                                <Col>
                                    <Toggle
                                        labels={[t('record_chapters_add_checkbox')]}
                                        selected={addChapters ? 0 : -1}
                                        onSelect={() => setAddChapters(selected => !selected)}
                                        disabled={disabled}
                                    >
                                        <RecordChapters
                                            chapters={record.chapters}
                                            refresh={refreshChapters}
                                            onChange={(chapters) => onChange('chapters', chapters)}
                                            message={validationMessages?.chapters}
                                            disabled={disabled}
                                        />
                                    </Toggle>
                                </Col>
                            </Row>
                        </Row>
                    </Form.Group>
                </Container>
            </div>
            {subtitles && subtitles.length > 0 && (
                <div className="record-form-section">
                    <h2 className="record-form-section-header">{t('record_form_section_subtitle_downloads')}</h2>
                    <Row className="mb-4">
                        <Col>
                            <RecordSubtitleDownloadLinks
                                onChange={onSubtitleChange}
                                media={record.media || []}
                                subtitles={subtitles}
                                resetSubtitleDownloadLinks={resetSubtitleDownloadLinks}
                                disabled={disabled}
                            />
                        </Col>
                    </Row>
                </div>
            )}
            <RecordChapterDownloadLinks
                identifier={record.identifier}
                refresh={refreshChapters}
                chapters={record.chapters}
                onChange={(chapters) => onChange('chapters', chapters)}
                disabled={disabled}
            />
        </Container>
    );
};

RecordForm.propTypes = {
    record: PropTypes.object,
    onChange: PropTypes.func,
    onSubtitleChange: PropTypes.func,
    resetSubtitleDownloadLinks: PropTypes.bool,
    refreshChapters: PropTypes.number,
    validationMessages: PropTypes.object,
    disabled: PropTypes.bool
};

export default RecordForm;
